import { useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, Outlet, useOutletContext, useParams } from "react-router";
import type { StudyDto } from "@gazelab/shared";
import { api, errorMessage } from "../../services/api.js";
import { useAsync } from "../../lib/useAsync.js";
import { STUDY_STATUS_LABELS } from "../../lib/format.js";
import { Badge, ErrorAlert, Spinner } from "../../components/ui/index.js";
import { Icon, type IconName } from "../../components/ui/Icon.js";
import { useSidebarSlot } from "../../components/ui/Layout.js";

export interface StudyContext {
  study: StudyDto;
  reload: () => Promise<void>;
}

export const useStudy = () => useOutletContext<StudyContext>();

const sideClass = ({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
    isActive ? "bg-aqua text-ink" : "text-white/75 hover:bg-white/5 hover:text-white"
  }`;

const mobileTabClass = ({ isActive }: { isActive: boolean }) =>
  `whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium ${isActive ? "bg-sky-700 text-white" : "bg-white text-slate-700 ring-1 ring-slate-200"}`;

export function StudyLayout() {
  const { id = "" } = useParams();
  const study = useAsync(() => api.getStudy(id), [id]);
  const slot = useSidebarSlot();
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  if (study.loading && !study.data) return <Spinner />;
  if (!study.data) return <ErrorAlert error={study.error ?? "Estudio no encontrado"} />;
  const s = study.data;
  const link = `${window.location.origin}/participar/${s.code}`;
  const sessions = s.sessionCounts.webgazer.total + s.sessionCounts.synthetic.total;
  const sections: Array<{ to: string; label: string; icon: IconName; count?: number }> = [
    { to: "configuracion", label: "Configuración", icon: "sliders" },
    { to: "multimedia", label: "Multimedia", icon: "photo", count: s.imageCount },
    { to: "participantes", label: "Participantes", icon: "users", count: sessions },
    { to: "resultados", label: "Resultados", icon: "chart" },
    { to: "exportar", label: "Exportar", icon: "download" },
  ];

  async function act(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      await study.reload();
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("No se pudo copiar el enlace; cópialo manualmente.");
    }
  }

  const sidebarNav = (
    <nav aria-label="Secciones del estudio" className="space-y-0.5" data-tour="study-nav">
      <Link to="/estudios" className="mb-2 flex items-center gap-1 px-3 font-util text-[0.65rem] uppercase tracking-[0.22em] text-white/45 hover:text-white/80">
        <Icon name="back" className="h-3.5 w-3.5" /> Todos los estudios
      </Link>
      <div className="mb-2 rounded-xl bg-white/5 px-3 py-2.5">
        <p className="line-clamp-2 text-sm font-semibold leading-snug text-white" title={s.title}>
          {s.title}
        </p>
        <p className="mt-1 flex items-center gap-1.5 font-util text-[0.7rem] text-white/55">
          <span className={`h-1.5 w-1.5 rounded-full ${s.status === "active" ? "bg-emerald-400" : s.status === "closed" ? "bg-amber-400" : "bg-white/40"}`} />
          {STUDY_STATUS_LABELS[s.status]} · {s.code}
        </p>
      </div>
      {sections.map((sec) => (
        <NavLink key={sec.to} to={sec.to} className={sideClass}>
          <Icon name={sec.icon} />
          <span className="flex-1">{sec.label}</span>
          {sec.count !== undefined && <span className="font-util text-xs opacity-70">{sec.count}</span>}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div>
      {slot && createPortal(sidebarNav, slot)}
      {/* Encabezado compacto en una línea: título, estado, enlace y acción principal. */}
      <header className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <h1 className="truncate text-xl font-bold text-ink" title={s.title}>
            {s.title}
          </h1>
          <Badge tone={s.status === "active" ? "success" : s.status === "closed" ? "warning" : "neutral"}>{STUDY_STATUS_LABELS[s.status]}</Badge>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 rounded-full bg-white py-1 pl-3 pr-1 text-sm ring-1 ring-slate-200" data-tour="study-link">
            <code className="font-mono text-xs text-slate-500" data-testid="study-code">
              {s.code}
            </code>
            <a href={link} className="ml-1 font-mono text-sky-800 hover:underline" target="_blank" rel="noreferrer" data-testid="participant-link" title="Abrir el enlace de participación">
              /participar/{s.code}
            </a>
            <button type="button" className="ml-1 inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold text-sky-800 hover:bg-sky-50" onClick={copy}>
              <Icon name={copied ? "check" : "copy"} className="h-3.5 w-3.5" />
              {copied ? "¡Copiado!" : "Copiar enlace"}
            </button>
          </div>
          {(s.status === "draft" || s.status === "closed") && (
            <button type="button" className="btn-primary" onClick={() => act(() => api.activateStudy(s.id))} data-tour="study-status">
              Activar
            </button>
          )}
          {s.status === "active" && (
            <button type="button" className="btn-secondary" onClick={() => act(() => api.closeStudy(s.id))} data-tour="study-status">
              Cerrar estudio
            </button>
          )}
        </div>
      </header>
      {s.status !== "active" && (
        <p className="-mt-2 mb-3 text-xs text-slate-500">
          El enlace de participación solo funciona con el estudio <strong>activo</strong> (requiere al menos una imagen o un video activo y dos grupos).
        </p>
      )}
      <ErrorAlert error={error} />
      {/* Secciones en pantallas pequeñas (en escritorio están en el menú lateral). */}
      <nav aria-label="Secciones del estudio (móvil)" className="mb-4 flex gap-2 overflow-x-auto md:hidden">
        {sections.map((sec) => (
          <NavLink key={sec.to} to={sec.to} className={mobileTabClass}>
            {sec.label}
          </NavLink>
        ))}
      </nav>
      <Outlet context={{ study: s, reload: study.reload } satisfies StudyContext} />
    </div>
  );
}
